import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  limit
} from "firebase/firestore";
import { db } from "../services/firebase";
import { getQuestions } from '../services/firebase/forum';

/**
 * Custom hook for managing forum state with optimized loading
 * Provides persistent state between navigation without complex caching
 */
const useForumState = () => {
  // Core state
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true for immediate UI feedback
  const [error, setError] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [tags, setTags] = useState([]);
  const [showMyPostsOnly, setShowMyPostsOnly] = useState(false);
  
  // Simple state tracking
  const lastFetchParamsRef = useRef(null);
  const abortControllerRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const realtimeUnsubscribeRef = useRef(null);
  
  // Check if filters have changed
  const filtersChanged = useCallback((newParams) => {
    if (!lastFetchParamsRef.current) return true;
    
    const lastParams = lastFetchParamsRef.current;
    return (
      lastParams.searchTerm !== newParams.searchTerm ||
      lastParams.sortBy !== newParams.sortBy ||
      JSON.stringify(lastParams.tags) !== JSON.stringify(newParams.tags) ||
      lastParams.showMyPostsOnly !== newParams.showMyPostsOnly
    );
  }, []);
  
  // Simplified fetch function without caching
  const fetchQuestions = useCallback(async (params, isLoadMore = false) => {
    const {
      searchTerm: paramSearchTerm = '',
      sortBy: paramSortBy = 'recent',
      tags: paramTags = [],
      showMyPostsOnly: paramShowMyPostsOnly = false,
      userId = null
    } = params;
    
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }
      
      const options = {
        limit: 20,
        lastVisible: isLoadMore ? lastVisible : null,
        sortBy: paramSortBy,
        searchTerm: paramSearchTerm,
        tags: paramTags,
        status: 'active',
        authorId: paramShowMyPostsOnly ? userId : null
      };
      
      console.log('🔥 Fetching forum questions from database', options);
      const result = await getQuestions(options);
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return { success: false, error: 'Request cancelled' };
      }
      
      if (result.success) {
        const newQuestions = result.data.questions;
        
        if (isLoadMore) {
          const updatedQuestions = [...questions, ...newQuestions];
          setQuestions(updatedQuestions);
        } else {
          setQuestions(newQuestions);
        }
        
        setLastVisible(result.data.lastVisible);
        setHasMore(result.data.hasMore);
        
        // Store last successful fetch params
        lastFetchParamsRef.current = params;
        hasLoadedRef.current = true;
        
        return { success: true };
      } else {
        setError(result.error || 'Failed to load questions');
        return { success: false, error: result.error };
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Request cancelled' };
      }
      
      console.error('Error fetching questions:', error);
      setError('An unexpected error occurred');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
      setLoadingMore(false);
      abortControllerRef.current = null;
    }
  }, [lastVisible, questions]);
  
  // Load more questions
  const loadMoreQuestions = useCallback(async (currentUserId) => {
    if (loadingMore || !hasMore) return;
    
    const params = {
      searchTerm,
      sortBy,
      tags,
      showMyPostsOnly,
      userId: currentUserId
    };
    
    await fetchQuestions(params, true);
  }, [fetchQuestions, searchTerm, sortBy, tags, showMyPostsOnly, loadingMore, hasMore]);
  
  // Refresh questions (force refresh)
  const refreshQuestions = useCallback(async (currentUserId) => {
    const params = {
      searchTerm,
      sortBy,
      tags,
      showMyPostsOnly,
      userId: currentUserId,
      forceRefresh: true
    };
    
    // Reset pagination and loaded state
    setLastVisible(null);
    setHasMore(true);
    hasLoadedRef.current = false;
    
    return await fetchQuestions(params, false);
  }, [fetchQuestions, searchTerm, sortBy, tags, showMyPostsOnly]);
  
  // Load questions with current filter state
  const loadQuestions = useCallback(async (currentUserId, forceRefresh = false) => {
    const params = {
      searchTerm,
      sortBy,
      tags,
      showMyPostsOnly,
      userId: currentUserId,
      forceRefresh
    };
    
    // Only load if we haven't loaded yet, filters changed, or force refresh
    const shouldLoad = !hasLoadedRef.current || filtersChanged(params) || forceRefresh;
    
    if (!shouldLoad) {
      console.log('🎯 Skipping load - posts already loaded and filters unchanged');
      // Ensure loading is false when using cached data
      setLoading(false);
      return { success: true, fromCache: true };
    }
    
    // Reset pagination for new filter or force refresh
    if (filtersChanged(params) || forceRefresh) {
      setLastVisible(null);
      setHasMore(true);
    }
    
    // Use requestIdleCallback to avoid blocking navigation if available
    if (window.requestIdleCallback && !forceRefresh) {
      return new Promise((resolve) => {
        window.requestIdleCallback(async () => {
          const result = await fetchQuestions(params, false);
          resolve(result);
        });
      });
    }
    
    return await fetchQuestions(params, false);
  }, [fetchQuestions, searchTerm, sortBy, tags, showMyPostsOnly, filtersChanged]);
  
  // Add new question to the list (optimistic update)
  const addNewQuestion = useCallback((newQuestion) => {
    setQuestions(prev => {
      // Check if question already exists (prevent duplicates)
      const exists = prev.some(q => q.id === newQuestion.id);
      if (exists) return prev;
      
      // Add to the beginning of the list for 'recent' sort
      if (sortBy === 'recent') {
        return [newQuestion, ...prev];
      }
      
      // For other sort types, we should refresh to maintain correct order
      return prev;
    });
  }, [sortBy]);
  
  // Update question in the list (for like updates, reply counts, etc.)
  const updateQuestion = useCallback((questionId, updates) => {
    setQuestions(prev => 
      prev.map(q => 
        q.id === questionId 
          ? { ...q, ...updates }
          : q
      )
    );
  }, []);
  
  // Clear state (useful for logout or major state changes)
  const clearCache = useCallback(() => {
    lastFetchParamsRef.current = null;
    hasLoadedRef.current = false;
    setQuestions([]);
    setLastVisible(null);
    setHasMore(true);
  }, []);
  
  // Filter update functions - reset loaded state when filters change
  const updateSearchTerm = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    hasLoadedRef.current = false;
  }, []);
  
  const updateSortBy = useCallback((newSortBy) => {
    setSortBy(newSortBy);
    hasLoadedRef.current = false;
  }, []);
  
  const updateTags = useCallback((newTags) => {
    setTags(newTags);
    hasLoadedRef.current = false;
  }, []);
  
  const updateShowMyPostsOnly = useCallback((showMyPosts) => {
    setShowMyPostsOnly(showMyPosts);
    hasLoadedRef.current = false;
  }, []);
  
  // Memoized filter state for components
  const filterState = useMemo(() => ({
    searchTerm,
    sortBy,
    tags,
    showMyPostsOnly
  }), [searchTerm, sortBy, tags, showMyPostsOnly]);
  
  // Set up real-time listener for new questions (only when 'recent' sort is active)
  const setupRealtimeListener = useCallback((userId) => {
    // Only set up real-time listeners for recent sort to avoid conflicts with other sorting/filtering
    if (sortBy !== 'recent' || !userId) {
      return;
    }

    // Clean up existing listener
    if (realtimeUnsubscribeRef.current) {
      realtimeUnsubscribeRef.current();
      realtimeUnsubscribeRef.current = null;
    }

    // Build query for recent questions only
    const questionsQuery = query(
      collection(db, 'forum_questions'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
      limit(50) // Limit to prevent excessive data
    );

    console.log('🔥 [FORUM REALTIME] Setting up real-time listener for new questions');

    const unsubscribe = onSnapshot(
      questionsQuery,
      (snapshot) => {
        if (!snapshot.metadata.hasPendingWrites) {
          // Only update if this is a server update, not a local write
          const realtimeQuestions = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          console.log('🔥 [FORUM REALTIME] Received real-time update:', realtimeQuestions.length, 'questions');

          // Update questions list with real-time data
          setQuestions(realtimeQuestions);
        }
      },
      (error) => {
        console.error('🔥 [FORUM REALTIME] Error in real-time listener:', error);
      }
    );

    realtimeUnsubscribeRef.current = unsubscribe;
  }, [sortBy]);

  // Set up real-time listener when questions are loaded and sort is 'recent'
  useEffect(() => {
    // Always cleanup existing listener first
    if (realtimeUnsubscribeRef.current) {
      realtimeUnsubscribeRef.current();
      realtimeUnsubscribeRef.current = null;
    }

    // Only set up real-time listener after initial load and for 'recent' sort
    if (hasLoadedRef.current && sortBy === 'recent' && questions.length > 0) {
      // Get the userId from the last fetch params
      const userId = lastFetchParamsRef.current?.userId;
      if (userId) {
        setupRealtimeListener(userId);
      }
    }

    // Always cleanup on unmount or dependency change
    return () => {
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
      }
    };
  }, [sortBy, setupRealtimeListener]);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    if (realtimeUnsubscribeRef.current) {
      try {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
      } catch (error) {
        console.warn('Error unsubscribing from forum real-time listener:', error);
      }
    }
  }, []);
  
  return {
    // Data state
    questions,
    loading,
    error,
    hasMore,
    loadingMore,
    
    // Filter state
    filterState,
    searchTerm,
    sortBy,
    tags,
    showMyPostsOnly,
    
    // Actions
    loadQuestions,
    loadMoreQuestions,
    refreshQuestions,
    addNewQuestion,
    updateQuestion,
    
    // Filter actions
    updateSearchTerm,
    updateSortBy,
    updateTags,
    updateShowMyPostsOnly,
    
    // Utility
    clearCache,
    cleanup,
    setupRealtimeListener
  };
};

export default useForumState;
